import { useRef, useState } from "react"
import { errorToast, toast } from "@/v2-ui/toast"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import LayerCreateSourceBar from "@/v2-backoffice-client/layer/create/LayerCreateSourceBar"
import LayerCreateSourceUsage from "@/v2-backoffice-client/layer/create/LayerCreateSourceUsage"
import LayerCreateIdentity from "@/v2-backoffice-client/layer/create/LayerCreateIdentity"
import LayerCreateCountries from "@/v2-backoffice-client/layer/create/LayerCreateCountries"
import LayerWorkbench from "@/v2-backoffice-client/layer/update/LayerWorkbench"
import {
  getLayerEditorModelPath,
  type LayerEditorField,
} from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"
import { useLayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata.hooks"
import { inferConfigurationLayerType } from "@/v2-backoffice-client/layer/service/layer.service.metadata"
import { useLayerCreateMutation } from "@/v2-backoffice-client/layer/create/layer.create.mutation"
import {
  buildStarterConfiguration,
  deriveIdentity,
  patchConfigurationSublayers,
} from "@/v2-backoffice-client/layer/create/layer.create.prefill"
import {
  LAYER_CREATE_MANUAL_CONFIGURATION,
  LAYER_CREATE_PREVIEW_CODE,
} from "@/v2-backoffice-client/layer/create/layer.create.constants"
import { FEATURE_CODE_PATTERN } from "@/v2-backoffice-client/feature/feature.constants"
import { setLayerAiHandoff } from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type { LayerAiChatTurn } from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type {
  LayerAiExternalPrompt,
  LayerAiSuggestableField,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"
import { LAYER_QUERY_NAME } from "@/v2-backoffice-client/layer/layer.constants"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

const CREATE_FIELDS: LayerEditorField[] = [
  "Configuration",
  "Filters",
  "Interactions",
  "Export",
  "Table",
]

/** Blob editors start like the update tabs render an empty blob: "null". */
const EMPTY_BLOB_TEXTS: Record<LayerEditorField, string> = {
  Configuration: LAYER_CREATE_MANUAL_CONFIGURATION,
  Filters: "null",
  Interactions: "null",
  Export: "null",
  Table: "null",
}

const EMPTY_IDENTITY = {
  featureCode: "MapLayer_NO_",
  name: "",
  description: "",
}

const IDENTITY_PROMPT_TEXT =
  "Re-propose the layer's identity — feature code, name and description — for the current configuration and business direction."

/** "null"/empty text counts as no content (tab dot + create payload). */
function hasBlobContent(text: string) {
  const trimmed = text.trim()
  return trimmed !== "" && trimmed !== "null"
}

/**
 * The guided create flow, as a wide sheet with the exact same workbench as
 * layer editing: URL in, layer out.
 *
 * Paste a source url → the probe prefills identity, type and a starter
 * configuration; the sheet flags layers already built on the source; the
 * AI assistant (create mode) ideates business cases and can propose the
 * whole setup including identity; the live preview renders the draft as it
 * evolves. Every blob has its own editor tab INSIDE the workbench's editor
 * pane — switching tabs swaps only the edited document while the preview
 * and the assistant stay put; AI-attached Filters/Table/Export land
 * visibly in their tabs (dot-marked), editable before create. All of it is
 * optional: every prefill is editable and the manual path stays a
 * first-class citizen. Nothing exists server-side until "Create layer":
 * the row lands disabled in the review queue and the real layer sheet
 * opens, inheriting the AI conversation.
 */
function LayerCreateSheet() {
  const opener = useAppViewSheetOpener()

  const [probedUrl, setProbedUrl] = useState<string | null>(null)
  const {
    metadata,
    error: probeError,
    isLoading,
  } = useLayerServiceMetadata(probedUrl)

  const [identity, setIdentity] = useState(EMPTY_IDENTITY)
  const [layerTypeCode, setLayerTypeCode] = useState("feature")
  // Where the layer is offered. Deliberately empty at first: a layer with
  // no country is hidden from every customer, so the pick is a decision the
  // operator makes, not a default they inherit. Create refuses without one.
  const [countryCodes, setCountryCodes] = useState<ReadonlySet<number>>(
    () => new Set(),
  )
  const [hasTriedWithoutCountry, setHasTriedWithoutCountry] = useState(false)
  const [visibleIds, setVisibleIds] = useState<ReadonlySet<
    number | string
  > | null>(null)
  // Two text records per blob: `seedTexts` is what the editors mount/reset
  // on (probe landed, type changed, AI attached a blob); `blobTexts` is the
  // live mirror of every tab (via onDraftChange) — the create payload and
  // the AI diff base read from it.
  const [seedTexts, setSeedTexts] = useState(EMPTY_BLOB_TEXTS)
  const [blobTexts, setBlobTexts] = useState(EMPTY_BLOB_TEXTS)
  // Conversation mirror for the post-create handoff; a ref — it renders
  // nothing here, it only travels to the created layer's assistant.
  const turnsRef = useRef<LayerAiChatTurn[]>([])
  // The identity form's "suggest with AI" pushes through here; the
  // workbench opens the assistant and auto-sends it.
  const [identityPrompt, setIdentityPrompt] =
    useState<LayerAiExternalPrompt | null>(null)

  function setBlob(field: LayerEditorField, text: string, reseed: boolean) {
    setBlobTexts((current) => ({ ...current, [field]: text }))
    if (reseed) setSeedTexts((current) => ({ ...current, [field]: text }))
  }

  /**
   * The configuration's shape wins over the Type select: an applied AI
   * suggestion (or a hand edit) that turns the draft into another layer
   * type must flip the type too, or the preview builds the wrong layer
   * class — a group without `layers` renders as NOTHING, with no error.
   * The current text is carried into the seed because the editor model is
   * per-type (schema binding): the remount must show the new draft, and
   * reseeding from the template would wipe the very content that flipped
   * the shape.
   */
  function syncTypeToDraft(text: string) {
    const inferred = inferConfigurationLayerType(text)
    if (!inferred || inferred === layerTypeCode) return
    setLayerTypeCode(inferred)
    setSeedTexts((current) => ({ ...current, Configuration: text }))
  }

  // Prefill once per probe result (render-adjust, no effect lag).
  const [prevMetadata, setPrevMetadata] = useState(metadata)
  if (metadata !== prevMetadata) {
    setPrevMetadata(metadata)
    if (metadata && probedUrl) {
      const starter = JSON.stringify(
        buildStarterConfiguration(
          metadata,
          probedUrl,
          metadata.layerTypeCode,
          null,
        ),
        null,
        2,
      )
      setIdentity(deriveIdentity(metadata))
      setLayerTypeCode(metadata.layerTypeCode)
      setVisibleIds(null)
      setSeedTexts({ ...EMPTY_BLOB_TEXTS, Configuration: starter })
      setBlobTexts({ ...EMPTY_BLOB_TEXTS, Configuration: starter })
    }
  }

  function reseedConfiguration(
    nextType: string,
    nextVisibleIds: ReadonlySet<number | string> | null,
  ) {
    if (!metadata || !probedUrl) return
    setBlob(
      "Configuration",
      JSON.stringify(
        buildStarterConfiguration(
          metadata,
          probedUrl,
          nextType,
          nextVisibleIds,
        ),
        null,
        2,
      ),
      true,
    )
  }

  const [createLayer, { loading: isCreating, error: createError }] =
    useLayerCreateMutation({
      // A fresh row invalidates the cached lists, counts and prod chips.
      refetchQueries: [
        LAYER_QUERY_NAME,
        LAYER_COUNTS_QUERY_NAME,
        LAYER_AUTHORING_STATUSES_QUERY_NAME,
      ],
    })
  errorToast(createError, { title: "Failed to create the layer" })

  /** Parses a tab's live text; throws with the tab's name for the toast. */
  function parseBlob(field: LayerEditorField): unknown {
    const text = blobTexts[field]
    if (!hasBlobContent(text)) return undefined
    try {
      return JSON.parse(text) ?? undefined
    } catch {
      throw new Error(`The ${field} tab does not contain valid JSON.`)
    }
  }

  async function create() {
    const featureCode = identity.featureCode.trim()
    if (!FEATURE_CODE_PATTERN.test(featureCode)) {
      toast({
        title: "Fix the feature code first",
        description:
          "Letters, digits and underscores only, starting with a letter.",
        variant: "destructive",
      })
      return
    }
    if (!identity.name.trim()) {
      toast({
        title: "Name the layer first",
        description: "The en-GB name is what customers see in the chooser.",
        variant: "destructive",
      })
      return
    }
    if (countryCodes.size === 0) {
      setHasTriedWithoutCountry(true)
      toast({
        title: "Pick at least one country",
        description:
          "A layer with no country is hidden from every customer. Tick the countries it is offered in, or select all.",
        variant: "destructive",
      })
      return
    }

    let blobs: Partial<Record<LayerEditorField, unknown>>
    try {
      blobs = {
        Configuration: parseBlob("Configuration"),
        Filters: parseBlob("Filters"),
        Interactions: parseBlob("Interactions"),
        Export: parseBlob("Export"),
        Table: parseBlob("Table"),
      }
    } catch (parseError) {
      toast({
        title: "Fix the JSON first",
        description:
          parseError instanceof Error ? parseError.message : "Invalid JSON.",
        variant: "destructive",
      })
      return
    }
    if (!blobs.Configuration) {
      toast({
        title: "Configuration is required",
        description: "The Configuration tab must contain a JSON object.",
        variant: "destructive",
      })
      return
    }

    const result = await createLayer({
      variables: {
        input: {
          FeatureCode: featureCode,
          Name: identity.name.trim(),
          Description: identity.description.trim() || undefined,
          LayerTypeCode: layerTypeCode,
          Configuration: blobs.Configuration,
          Filters: blobs.Filters,
          Interactions: blobs.Interactions,
          Export: blobs.Export,
          Table: blobs.Table,
          CountryCodes: [...countryCodes],
        },
      },
    })

    const created = result.data?.createLayer
    if (!created) return

    // The conversation follows the layer: its sheet's assistant claims it
    // and the operator continues ideating where they left off.
    if (turnsRef.current.length > 0) {
      setLayerAiHandoff({
        targetFeatureCode: created.FeatureCode,
        mode: "wizard",
        turns: turnsRef.current,
      })
    }

    toast({
      title: "Layer created.",
      description: `It landed in the review queue — invisible to customers until it goes live, then offered in ${countryCodes.size} ${countryCodes.size === 1 ? "country" : "countries"}.`,
    })
    opener.open("layer", created.FeatureCode)
  }

  /** The AI diff base: parsed live blobs (invalid tabs read as empty). */
  function parseAiBlobs(): Partial<Record<LayerAiSuggestableField, unknown>> {
    const parsed: Partial<Record<LayerAiSuggestableField, unknown>> = {}
    for (const field of CREATE_FIELDS) {
      if (field === "Configuration") continue
      try {
        parsed[field] = hasBlobContent(blobTexts[field])
          ? JSON.parse(blobTexts[field])
          : undefined
      } catch {
        parsed[field] = undefined
      }
    }
    return parsed
  }

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title>New layer</ViewSheet.Title>
      </ViewSheet.Header>

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <LayerCreateSourceBar
          isLoading={isLoading}
          metadata={metadata}
          error={probeError}
          onProbe={setProbedUrl}
        />

        {probedUrl && metadata && <LayerCreateSourceUsage url={probedUrl} />}

        <LayerCreateIdentity
          identity={identity}
          onIdentityChange={setIdentity}
          layerTypeCode={layerTypeCode}
          onLayerTypeChange={(next) => {
            setLayerTypeCode(next)
            reseedConfiguration(next, visibleIds)
          }}
          sublayers={metadata?.sublayers ?? []}
          visibleIds={visibleIds}
          onVisibleIdsChange={(next) => {
            setVisibleIds(next)
            // Patch the draft in place — toggling a sublayer must never
            // wipe the operator's (or the AI's) edits. Only an unparseable
            // draft falls back to a full re-seed.
            const patched =
              metadata && probedUrl
                ? patchConfigurationSublayers(
                    blobTexts.Configuration,
                    metadata,
                    probedUrl,
                    layerTypeCode,
                    next,
                  )
                : null
            if (patched != null) setBlob("Configuration", patched, true)
            else reseedConfiguration(layerTypeCode, next)
          }}
          onAiIdentity={
            probedUrl && metadata
              ? () =>
                  setIdentityPrompt({
                    id: Date.now(),
                    text: IDENTITY_PROMPT_TEXT,
                  })
              : undefined
          }
        />

        <LayerCreateCountries
          selected={countryCodes}
          onChange={setCountryCodes}
          isInvalid={hasTriedWithoutCountry}
        />

        <LayerWorkbench
          // A successfully probed source is a new editing session: remount
          // resets the editors onto the fresh seeds and opens the assistant.
          key={metadata ? probedUrl : "manual"}
          className="min-h-[440px]"
          field="Configuration"
          layerTypeCode={layerTypeCode}
          modelPath={`create/${getLayerEditorModelPath("Configuration", layerTypeCode)}`}
          value={seedTexts.Configuration}
          // Every blob as a tab inside the editor pane: switching swaps the
          // edited document while the preview and assistant stay put.
          documents={CREATE_FIELDS.map((field) => ({
            field,
            value: seedTexts[field],
            modelPath: `create/${getLayerEditorModelPath(field, layerTypeCode)}`,
            hasContent:
              field !== "Configuration" && hasBlobContent(blobTexts[field]),
          }))}
          isSaving={isCreating}
          onSave={() => void create()}
          onDocumentDraftChange={(field, text) => {
            setBlob(field, text, false)
            if (field === "Configuration") syncTypeToDraft(text)
          }}
          saveLabel="Create layer"
          canSaveClean
          serviceUrl={probedUrl}
          previewAutoApplyMs={1200}
          defaultAssistantOpen={Boolean(metadata)}
          preview={{
            FeatureCode: LAYER_CREATE_PREVIEW_CODE,
            Name: identity.name || "New layer",
            LayerTypeCode: layerTypeCode,
            CreatedAt: new Date(0).toISOString(),
          }}
          // The assistant reasons over the probed source; without one (the
          // manual path) there is nothing to ground in.
          assist={
            probedUrl && metadata
              ? {
                  sourceUrl: probedUrl,
                  name: identity.name || undefined,
                  blobs: parseAiBlobs(),
                  externalPrompt: identityPrompt,
                  onSaveField: (aiField, value) => {
                    // Land it in the tab's editor (dot appears); the
                    // operator reviews/edits it there.
                    setBlob(aiField, JSON.stringify(value, null, 2), true)
                    return Promise.resolve(value)
                  },
                  onIdentity: (proposal) => {
                    setIdentity({
                      featureCode: proposal.FeatureCode,
                      name: proposal.Name,
                      description: proposal.Description ?? "",
                    })
                  },
                  onTurns: (turns) => {
                    turnsRef.current = turns
                  },
                }
              : undefined
          }
        />
      </div>
    </>
  )
}

export default LayerCreateSheet
